import React, { useState } from 'react';
import { BookOpen, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { useParentCases } from '../hooks/api/useCases';
import { useDeleteCase } from '../hooks/api/useCaseMutations';
import { useAuthContext } from '../contexts/useAuthContext';
import { useModal } from '../contexts/useModal';
import { CaseCardSkeletonGrid } from '../components/skeletons';
import { CaseCard } from '../components/common/CaseCard';
import ConfirmDeleteModal from '../components/Modal/ConfirmDeleteModal';
import { Pagination } from '@tutor/ui';
import TagBar from '@/components/common/TagBar';
import type { ParentCaseTab } from '@/constants/parentCaseStatus';
import { useI18n } from '@/i18n';
import { useNavigate } from '@/i18n/navigation';

const MyCasesPage: React.FC = () => {
  const { profile, firebaseUser } = useAuthContext();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const { openParentRegistration } = useModal();
  const [selectedTab, setSelectedTab] = useState<ParentCaseTab>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [caseToDelete, setCaseToDelete] = useState<string | null>(null);
  const deleteCase = useDeleteCase();

  // Fetch parent's own cases — server scopes by parent and filters by tab
  const { data, isLoading, error } = useParentCases({ tab: selectedTab, page: currentPage, limit: 9 });

  // Parallel queries for tab counts (limit: 1 to minimise payload)
  const { data: allData } = useParentCases({ tab: 'all', page: 1, limit: 1 });
  const { data: underReviewData } = useParentCases({ tab: 'underReview', page: 1, limit: 1 });
  const { data: matchingData } = useParentCases({ tab: 'matching', page: 1, limit: 1 });
  const { data: inProgressData } = useParentCases({ tab: 'inProgress', page: 1, limit: 1 });
  const { data: matchedData } = useParentCases({ tab: 'matched', page: 1, limit: 1 });
  const { data: actionNeededData } = useParentCases({ status: 'action_needed', page: 1, limit: 1 });
  const { data: rejectedData } = useParentCases({ tab: 'rejected', page: 1, limit: 1 });
  const hasActionNeededCases = (actionNeededData?.pagination.total ?? 0) > 0;
  const isZh = locale === 'zh-CN';

  const cases = data?.cases ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  // Redirect if not a parent
  if (!firebaseUser || profile?.role !== 'parent') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {isZh ? '无法访问' : 'Access Denied'}
          </h2>
          <p className="text-gray-600">{isZh ? '只有家长可以访问此页面。' : 'Only parents can access this page.'}</p>
        </div>
      </div>
    );
  }

  const handleEditCase = (caseId: string) => {
    navigate(`/parent/edit-case/${caseId}`);
  };

  const handleDeleteCase = (caseId: string) => {
    setCaseToDelete(caseId);
  };

  const handleDeleteConfirm = async () => {
    if (!caseToDelete) return;

    try {
      await deleteCase.mutateAsync(caseToDelete);
      toast.success(isZh ? '个案已成功删除！' : 'Case deleted successfully!');
      setCaseToDelete(null);
    } catch (error) {
      logger.error('MyCasesPage', 'Failed to delete case', error);
      toast.error(isZh ? '删除个案失败' : 'Failed to delete case');
    }
  };

  const handleViewDetails = (caseId: string) => {
    navigate(`/cases/${caseId}?source=parent_my_cases`);
  };

  const handleResubmit = (caseId: string) => {
    // Resubmit flow: edit the case, then change status back to pending_review
    navigate(`/parent/edit-case/${caseId}?resubmit=true`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="h-8 w-8" />
            <h1 className="text-3xl md:text-4xl font-bold">{isZh ? '我的个案' : 'My Cases'}</h1>
          </div>
          <p className="text-white/90 max-w-2xl">
            {isZh ? '管理你的补习个案，并追踪处理进度' : 'Manage your tutoring cases and track their progress'}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Header with Create Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-900">{isZh ? '你的个案' : 'Your Cases'}</h2>
          <button
            onClick={() => openParentRegistration()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
          >
            <Plus className="h-5 w-5" />
            {isZh ? '发布新个案' : 'Post New Case'}
          </button>
        </div>

        {/* Tabs */}
        <TagBar
          items={[
            { id: 'all' as ParentCaseTab, label: isZh ? '全部个案' : 'All Cases', count: allData?.pagination.total ?? 0 },
            { id: 'underReview' as ParentCaseTab, label: isZh ? '审核中' : 'Under Review', count: underReviewData?.pagination.total ?? 0 },
            { id: 'matching' as ParentCaseTab, label: isZh ? '配对中' : 'Matching', count: matchingData?.pagination.total ?? 0 },
            {
              id: 'inProgress' as ParentCaseTab,
              label: isZh ? '处理中' : 'In Progress',
              count: inProgressData?.pagination.total ?? 0,
              hasAlertDot: hasActionNeededCases,
            },
            { id: 'matched' as ParentCaseTab, label: isZh ? '已配对' : 'Matched', count: matchedData?.pagination.total ?? 0 },
            { id: 'rejected' as ParentCaseTab, label: isZh ? '已拒绝' : 'Rejected', count: rejectedData?.pagination.total ?? 0 },
          ]}
          activeId={selectedTab}
          onChange={(tab) => {
            setSelectedTab(tab);
            setCurrentPage(1);
          }}
          className="mb-8 border-b border-gray-200"
          innerClassName="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0"
          itemClassName="pb-3 px-4 font-medium text-sm md:text-base transition-colors relative whitespace-nowrap"
          activeClassName="text-primary border-b-2 border-primary"
          inactiveClassName="text-gray-600 hover:text-gray-900"
          showCounts
        />

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">{isZh ? '加载个案时出错' : 'Error loading cases'}</h3>
              <p className="text-red-700 text-sm mt-1">{error instanceof Error ? error.message : isZh ? '发生错误' : 'An error occurred'}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <CaseCardSkeletonGrid count={9} />
        )}

        {/* Empty State */}
        {!isLoading && cases.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-block p-3 bg-gray-100 rounded-full mb-4">
              <BookOpen className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {selectedTab === 'all' && (isZh ? '还没有个案' : 'No cases yet')}
              {selectedTab === 'underReview' && (isZh ? '没有审核中的个案' : 'No cases under review')}
              {selectedTab === 'matching' && (isZh ? '没有正在配对中的个案' : 'No cases currently matching')}
              {selectedTab === 'inProgress' && (isZh ? '没有进行中的个案' : 'No cases in progress')}
              {selectedTab === 'matched' && (isZh ? '还没有已配对的个案' : 'No matched cases yet')}
              {selectedTab === 'rejected' && (isZh ? '没有被拒绝的个案' : 'No rejected cases')}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {selectedTab === 'all' && (isZh ? '先发布一个新的补习个案，开始寻找合适导师' : 'Start by creating a new tutoring case to find the perfect tutor')}
              {selectedTab === 'underReview' && (isZh ? '等待管理员审核的个案会显示在这里' : 'Your cases will appear here while awaiting admin review')}
              {selectedTab === 'matching' && (isZh ? '我们正在为你配对合适导师的个案会显示在这里' : 'Your cases will appear here while we are matching suitable tutors')}
              {selectedTab === 'inProgress' && (isZh ? '需要你处理的个案会显示在这里' : 'Cases that need your action will appear here')}
              {selectedTab === 'matched' && (isZh ? '已成功配对导师的个案会显示在这里' : 'Your cases will appear here after being matched with a tutor')}
              {selectedTab === 'rejected' && (isZh ? '很好，你目前没有被拒绝的个案' : "Great! You don't have any rejected cases")}
            </p>
            {selectedTab === 'all' && (
              <button
                onClick={() => openParentRegistration()}
                className="inline-flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
              >
                <Plus className="h-5 w-5" />
                {isZh ? '发布新个案' : 'Post New Case'}
              </button>
            )}
          </div>
        )}

        {/* Cases Grid */}
        {!isLoading && cases.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {cases.map((caseData) => (
                <CaseCard
                  key={caseData.id}
                  caseData={caseData}
                  onEdit={handleEditCase}
                  onDelete={handleDeleteCase}
                  onViewDetails={handleViewDetails}
                  onResubmit={handleResubmit}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!caseToDelete}
        onClose={() => setCaseToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={isZh ? '删除个案' : 'Delete Case'}
        message={isZh ? '你确定要删除这个个案吗？此操作无法撤销。' : 'Are you sure you want to delete this case? This action cannot be undone.'}
        confirmText={isZh ? '删除' : 'Delete'}
      />
    </div>
  );
};

export default MyCasesPage;
